/**
 * Root route component
 * Provides QueryClientProvider, ToastProvider, ErrorBoundary, and global layout
 */

import { createRootRoute, Outlet, useRouterState } from '@tanstack/react-router';
import { isValidDate, parseDateFromAPI, formatDateForAPI, getWeekStart } from '@/lib/utils/date';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ToastProvider } from '@/components/ui/ToastProvider';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { WeekProvider } from '@/contexts/WeekContext';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import { Sidebar } from '@/components/layout/Sidebar';

// Create optimized QueryClient instance
// Exported so it can be used in route guards for auth caching
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes - data stays fresh
      gcTime: 1000 * 60 * 30, // 30 minutes - cache retention (formerly cacheTime)
      refetchOnWindowFocus: false, // Don't refetch on window focus
      refetchOnReconnect: true, // Refetch on network reconnection
      retry: 1, // Retry failed requests once
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    },
    mutations: {
      retry: 1,
      retryDelay: 1000,
    },
  },
});

// Search params type for the root route (shared across all routes)
export interface RootSearchParams {
  week?: string; // Format: YYYY-MM-DD (Monday of the week)
  redirect?: string; // Used by auth guards for post-login redirect
  [key: string]: unknown;
}

// Validate and parse the week query parameter
// Preserves other search params for child routes (e.g., page for pagination)
function validateWeekSearch(search: Record<string, unknown>): RootSearchParams & Record<string, unknown> {
  const weekParam = search.week as string | undefined;
  const redirectParam = search.redirect as string | undefined;

  const result: RootSearchParams & Record<string, unknown> = {
    ...search, // Preserve other params for child routes
    week: undefined,
    redirect: redirectParam,
  };

  // Validate the week param is a valid date
  if (weekParam && isValidDate(weekParam)) {
    // Normalize to the Monday of that week
    const date = parseDateFromAPI(weekParam);
    const weekStart = getWeekStart(date);
    result.week = formatDateForAPI(weekStart);
  }

  return result;
}

export const Route = createRootRoute({
  validateSearch: validateWeekSearch,
  component: RootComponent,
});

function RootComponent() {
  const router = useRouterState();
  const isLoginPage = router.location.pathname === '/login';

  // Try to get user from route context (will be undefined on login page)
  const routeContext = router.matches[router.matches.length - 1]?.context as
    | { user?: { isOrganizer?: boolean; name?: string } }
    | undefined;
  const user = routeContext?.user;

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <WeekProvider>
            <div className="relative min-h-screen">
              {/* Sidebar - show on desktop for all pages except login */}
              {!isLoginPage && (
                <Sidebar isOrganizer={user?.isOrganizer} userName={user?.name} />
              )}
              {/* Main content area with responsive padding */}
              <div
                className={`${!isLoginPage ? 'pb-16 lg:pb-0 lg:pl-64' : ''}`}
              >
                <Outlet />
              </div>
              {/* Bottom Navigation - show on mobile/tablet for all pages except login */}
              {!isLoginPage && <BottomNavigation isOrganizer={user?.isOrganizer} />}
            </div>
          </WeekProvider>
        </ToastProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
